import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyForm = { email: "", name: "", password: "", active: true };

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null); // {mode:"create"} | {mode:"edit", user}
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(errDetail(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(emptyForm); setDialog({ mode: "create" }); };
  const openEdit = (u) => {
    setForm({ email: u.email, name: u.name || "", password: "", active: u.active !== false });
    setDialog({ mode: "edit", user: u });
  };

  const save = async () => {
    setBusy(true);
    try {
      if (dialog.mode === "create") {
        await api.post("/admin/users", { email: form.email, password: form.password, name: form.name });
        toast.success(`User ${form.email} created`);
      } else {
        const payload = { email: form.email, name: form.name, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${dialog.user.id}`, payload);
        toast.success(`User ${form.email} updated`);
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${confirmDelete.id}`);
      toast.success(`User ${confirmDelete.email} deleted`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(errDetail(e));
    } finally {
      setBusy(false);
    }
  };

  const statusBadge = (u) => {
    if (u.is_admin) return <Badge variant="outline" className="label-caps font-mono border-[hsl(var(--primary))] text-primary">Admin</Badge>;
    if (u.active === false) return <Badge variant="outline" className="label-caps font-mono border-border text-muted-foreground">Pending</Badge>;
    return <Badge variant="outline" className="label-caps font-mono border-[hsl(var(--profit))] text-profit">Active</Badge>;
  };

  return (
    <section className="border border-border bg-card" data-testid="admin-users-section">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <span className="label-caps text-muted-foreground">Users</span>
        <Button size="sm" data-testid="admin-create-user-btn" onClick={openCreate}
          className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
          <Plus size={14} weight="bold" className="mr-1" /> New User
        </Button>
      </div>
      <div className="p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left label-caps text-muted-foreground border-b border-border">
              <th className="pb-2 pr-4">Email</th>
              <th className="pb-2 pr-4">Name</th>
              <th className="pb-2 pr-4">Status</th>
              <th className="pb-2 pr-4">Created</th>
              <th className="pb-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border/50 last:border-b-0" data-testid={`admin-user-row-${u.email}`}>
                <td className="py-2.5 pr-4 font-mono">{u.email}</td>
                <td className="py-2.5 pr-4">{u.name}</td>
                <td className="py-2.5 pr-4">{statusBadge(u)}</td>
                <td className="py-2.5 pr-4 text-muted-foreground font-mono text-xs">{fmtDateTime(u.created_at)}</td>
                <td className="py-2.5 text-right whitespace-nowrap">
                  {!u.is_admin && (
                    <>
                      <button data-testid={`admin-edit-user-${u.email}`} onClick={() => openEdit(u)}
                        className="text-muted-foreground hover:text-foreground transition-colors duration-150 mr-3" title="Edit">
                        <PencilSimple size={16} />
                      </button>
                      <button data-testid={`admin-delete-user-${u.email}`} onClick={() => setConfirmDelete(u)}
                        className="text-muted-foreground hover:text-loss transition-colors duration-150" title="Delete">
                        <Trash size={16} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-muted-foreground mt-3">
          Users you create here are active immediately. Self-registered users must activate via email first.
          The admin account itself is configured via ADMIN_USERNAME / ADMIN_PASSWORD environment variables.
        </p>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-card border-border" data-testid="admin-user-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "create" ? "New User" : `Edit ${dialog?.user?.email}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-caps text-muted-foreground">Email</Label>
              <Input data-testid="admin-user-email-input" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Name</Label>
              <Input data-testid="admin-user-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">
                {dialog?.mode === "create" ? "Password (min 6 characters)" : "New password (leave empty to keep current)"}
              </Label>
              <Input data-testid="admin-user-password-input" type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
            </div>
            {dialog?.mode === "edit" && (
              <div className="flex items-center justify-between">
                <Label className="label-caps text-muted-foreground">Active (can log in)</Label>
                <Switch data-testid="admin-user-active-switch" checked={form.active}
                  onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button data-testid="admin-user-save-btn" onClick={save}
              disabled={busy || !form.email || (dialog?.mode === "create" && form.password.length < 6)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground">
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="bg-card border-border" data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete {confirmDelete?.email}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently deletes the user and all their trades, photos, exchange keys and notifications. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button data-testid="admin-delete-confirm-btn" onClick={remove} disabled={busy}
              className="bg-[hsl(var(--loss))] text-white hover:bg-[hsl(var(--loss))]/90">
              {busy ? "Deleting…" : "Delete user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
